import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ClipboardCheck, Save, AlertCircle, Eye, Pencil, Lock, CalendarPlus, Clock, Trash2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { warn as devWarn } from "@/lib/devlog";

const STATUS = [
  { id: "present", label: "P", title: "Present", cls: "bg-white text-brand-inkMuted border-brand-purple/15 hover:bg-[#F1F5FF]", active: "bg-[#22C55E] text-white border-[#22C55E]" },
  { id: "absent", label: "A", title: "Absent", cls: "bg-white text-brand-inkMuted border-brand-purple/15 hover:bg-[#F1F5FF]", active: "bg-[#EF4444] text-white border-[#EF4444]" },
  { id: "excuse", label: "E", title: "Excuse", cls: "bg-white text-brand-inkMuted border-brand-purple/15 hover:bg-[#F1F5FF]", active: "bg-[#F59E0B] text-white border-[#F59E0B]" },
];

// Parse a "YYYY-MM-DD" string as a LOCAL date (avoids UTC midnight drift).
function parseISO(iso) {
  if (iso instanceof Date) return new Date(iso.getFullYear(), iso.getMonth(), iso.getDate());
  const [y, m, d] = String(iso).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
// Get the Monday of the week containing the given date (always local).
function getMonday(d) {
  const dt = d instanceof Date ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : parseISO(d);
  const day = dt.getDay(); // 0=Sun..6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  dt.setDate(dt.getDate() + diff);
  return dt;
}
// Format a Date as "YYYY-MM-DD" using LOCAL components.
function fmtISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function shortDate(iso) {
  const d = parseISO(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function ClassAttendancePage() {
  const { selectedSchool, selectedBranch, branchesForSelectedSchool, setSelectedBranchId } = useBranch();
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [weekStart, setWeekStart] = useState(fmtISO(getMonday(new Date())));
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({}); // {date: {student_id: status}}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);  // {student}
  const [editing, setEditing] = useState(null);  // {student}
  const [makeUp, setMakeUp] = useState(null);    // {student}

  const branchId = selectedBranch?.id;

  // Load classes + sections when branch changes
  useEffect(() => {
    if (!branchId) { setClasses([]); setSections([]); return; }
    (async () => {
      try {
        const [{ data: c }, { data: s }] = await Promise.all([
          api.get("/classes", { params: { branch_id: branchId } }),
          api.get("/sections", { params: { branch_id: branchId } }),
        ]);
        const regular = (c || []).filter((cl) => cl.class_type === "regular" || cl.class_type === "modular");
        setClasses(regular);
        setSections(s || []);
        if (regular.length && !regular.find((cl) => cl.id === classId)) setClassId(regular[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
    // eslint-disable-next-line
  }, [branchId]);

  const sectionsForClass = useMemo(() => sections.filter((s) => s.class_id === classId), [sections, classId]);
  const currentSection = useMemo(() => sections.find((s) => s.id === sectionId), [sections, sectionId]);

  // Modular-class sections store SPECIFIC session dates (YYYY-MM-DD) in the
  // `schedule[].day` field via the "Generate dates" helper on Classes & Sections.
  // Regular-class sections store weekday names ("Monday", "Tuesday", …).
  // Detect the former by checking if ANY schedule entry looks like an ISO date.
  const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
  const scheduleIsDates = useMemo(
    () => (currentSection?.schedule || []).some((sl) => ISO_DATE_RE.test((sl.day || "").trim())),
    // eslint-disable-next-line
    [currentSection],
  );

  // Set of scheduled WEEKDAY names (lowercased) — used only for regular classes.
  const scheduledDays = useMemo(() => {
    if (scheduleIsDates) return new Set();
    return new Set((currentSection?.schedule || []).map((sl) => (sl.day || "").toLowerCase()));
  }, [currentSection, scheduleIsDates]);

  // Set of scheduled SESSION DATES (ISO) — used only for modular classes.
  const scheduledSessionDates = useMemo(() => {
    if (!scheduleIsDates) return new Set();
    return new Set((currentSection?.schedule || [])
      .map((sl) => (sl.day || "").trim())
      .filter((d) => ISO_DATE_RE.test(d)));
  }, [currentSection, scheduleIsDates]);

  // Map weekday-name (lowercase) -> "HH:MM – HH:MM" string for the header.
  // For modular classes, we key by ISO date instead of weekday.
  const dayTimeMap = useMemo(() => {
    const m = {};
    for (const sl of (currentSection?.schedule || [])) {
      const k = scheduleIsDates ? (sl.day || "").trim() : (sl.day || "").toLowerCase();
      if (!k) continue;
      const range = sl.time_start
        ? `${sl.time_start}${sl.time_end ? ` – ${sl.time_end}` : ""}`
        : "";
      m[k] = m[k] ? `${m[k]}, ${range}` : range;
    }
    return m;
  }, [currentSection, scheduleIsDates]);
  // Filter the 7 dates to only those the section is actually scheduled on.
  //   • Modular (date-based schedule): dates that appear in `scheduledSessionDates`.
  //   • Regular  (weekday-based)     : dates whose weekday is in `scheduledDays`.
  //   • No schedule at all           : fall back to all 7 days.
  const visibleDates = useMemo(() => {
    if (!data) return [];
    if (scheduleIsDates) {
      return data.dates.filter((d) => scheduledSessionDates.has(d));
    }
    if (scheduledDays.size === 0) return data.dates;
    return data.dates.filter((d) => {
      const name = parseISO(d).toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
      return scheduledDays.has(name);
    });
  }, [data, scheduledDays, scheduledSessionDates, scheduleIsDates]);
  useEffect(() => {
    if (sectionsForClass.length && !sectionsForClass.find((s) => s.id === sectionId)) {
      setSectionId(sectionsForClass[0].id);
    } else if (!sectionsForClass.length) {
      setSectionId("");
    }
    // eslint-disable-next-line
  }, [sectionsForClass]);

  const loadWeek = useCallback(async () => {
    if (!branchId || !sectionId) { setData(null); setDraft({}); return; }
    setLoading(true);
    try {
      const { data: d } = await api.get("/class-attendance", {
        params: { branch_id: branchId, section_id: sectionId, week_start: weekStart },
      });
      setData(d);
      setDraft(JSON.parse(JSON.stringify(d.marks || {})));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [branchId, sectionId, weekStart]);

  useEffect(() => { loadWeek(); }, [loadWeek]);

  const shiftWeek = (days) => {
    const d = parseISO(weekStart);
    d.setDate(d.getDate() + days);
    setWeekStart(fmtISO(getMonday(d)));
  };

  // A cell is locked once it has been saved (server returned a mark for it).
  const isSaved = (date, studentId) => Boolean(data?.marks?.[date]?.[studentId]);

  const setMark = (date, studentId, status) => {
    if (isSaved(date, studentId)) {
      toast.info("Already saved. Use the Edit button to change it.");
      return;
    }
    setDraft((prev) => {
      const cur = prev[date]?.[studentId];
      const next = { ...(prev[date] || {}) };
      if (cur === status) delete next[studentId]; else next[studentId] = status;
      return { ...prev, [date]: next };
    });
  };

  const saveDay = async (date) => {
    if (!data) return;
    setSaving(true);
    try {
      await api.put(`/class-attendance?branch_id=${branchId}`, {
        section_id: sectionId, date, marks: draft[date] || {},
      });
      toast.success(`Saved ${shortDate(date)}`);
      loadWeek();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveAll = async () => {
    if (!data) return;
    setSaving(true);
    try {
      for (const date of visibleDates) {
        await api.put(`/class-attendance?branch_id=${branchId}`, {
          section_id: sectionId, date, marks: draft[date] || {},
        });
      }
      toast.success("Week saved");
      loadWeek();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const stats = useMemo(() => {
    const total = data?.students?.length || 0;
    let p = 0, a = 0, e = 0;
    const today = fmtISO(new Date());
    const todayMarks = draft[today] || {};
    for (const v of Object.values(todayMarks)) {
      if (v === "present") p++; else if (v === "absent") a++; else if (v === "excuse") e++;
    }
    return { total, p, a, e, notMarked: Math.max(0, total - p - a - e) };
  }, [data, draft]);

  const weekLabel = data
    ? `${shortDate(data.dates[0])} – ${shortDate(data.dates[6])}`
    : (() => { const end = parseISO(weekStart); end.setDate(end.getDate() + 6); return `${shortDate(weekStart)} – ${shortDate(fmtISO(end))}`; })();

  return (
    <>
      <div className="mb-5">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Class Attendance</h1>
        <p className="text-brand-inkMuted mt-0.5 text-sm">Record and monitor student class attendance by section and schedule.</p>
      </div>

      {/* Filter bar */}
      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-3 sm:p-4 mb-4" data-testid="ca-filters">
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <Field label="SCHOOL">
            <ReadInput value={selectedSchool?.name || "—"} />
          </Field>
          <Field label="BRANCH">
            <select value={branchId || ""} onChange={(e) => setSelectedBranchId(e.target.value)} className="ca-select" data-testid="ca-branch">
              {branchesForSelectedSchool.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <Field label="CLASS / LEVEL">
            <select value={classId} onChange={(e) => setClassId(e.target.value)} className="ca-select" data-testid="ca-class">
              {classes.length === 0 ? <option value="">No regular classes</option> : classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="SECTION">
            <select value={sectionId} onChange={(e) => setSectionId(e.target.value)} className="ca-select" data-testid="ca-section">
              {sectionsForClass.length === 0 ? <option value="">No sections</option> : sectionsForClass.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="WEEK">
            <div className="inline-flex items-stretch rounded-2xl border border-brand-purple/20 bg-white overflow-hidden h-11" data-testid="ca-week">
              <button type="button" onClick={() => shiftWeek(-7)} className="px-3 hover:bg-brand-purpleLight" data-testid="ca-week-prev"><ChevronLeft className="w-4 h-4" /></button>
              <div className="px-5 flex items-center font-heading text-foreground bg-[#F6F8FE] border-x border-brand-purple/15 whitespace-nowrap">{weekLabel}</div>
              <button type="button" onClick={() => shiftWeek(7)} className="px-3 hover:bg-brand-purpleLight" data-testid="ca-week-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </Field>
        </div>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4" data-testid="ca-stats">
        <Stat label="TOTAL STUDENTS" value={stats.total} tone="default" />
        <Stat label="PRESENT" value={stats.p} tone="green" />
        <Stat label="ABSENT" value={stats.a} tone="red" />
        <Stat label="EXCUSE" value={stats.e} tone="orange" />
        <Stat label="NOT MARKED" value={stats.notMarked} tone="default" />
      </section>

      {/* Grid */}
      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden" data-testid="ca-grid-wrap">
        {!sectionId ? (
          <Empty title="Pick a class and section" subtitle="Class attendance is taken per section." />
        ) : loading ? (
          <p className="p-6 text-brand-inkMuted">Loading...</p>
        ) : (data?.students?.length || 0) === 0 ? (
          <Empty title="No students enrolled in this section." subtitle="Enroll students into this section to start marking attendance." />
        ) : scheduleIsDates && visibleDates.length === 0 ? (
          // Modular class with no sessions falling inside the current week —
          // give a specific, actionable message instead of a blank table.
          <Empty
            title="No modular sessions in this week."
            subtitle="Use the week arrows to jump to a week that contains a scheduled session, or edit the section on Classes & Sections to add more dates."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ca-grid">
              <thead className="bg-[#F6F8FE] text-brand-inkMuted">
                <tr>
                  <th className="text-left px-4 py-3 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap min-w-[220px] sticky left-0 bg-[#F6F8FE] z-10">STUDENT</th>
                  {visibleDates.map((d) => {
                    const weekday = parseISO(d).toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
                    // Modular schedules are keyed by ISO date; regular by weekday name.
                    const time = scheduleIsDates ? dayTimeMap[d] : dayTimeMap[weekday];
                    return (
                      <th key={d} className="text-center px-3 py-3 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">
                        <div>{parseISO(d).toLocaleDateString(undefined, { weekday: "short" }).toUpperCase()}</div>
                        <div className="text-[10px] font-normal text-brand-inkMuted/80 mt-0.5">{shortDate(d)}</div>
                        {time && (
                          <div className="text-[10px] font-normal text-brand-purple/80 mt-1 inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {time}
                          </div>
                        )}
                        <div className="mt-1.5">
                          <button
                            onClick={() => saveDay(d)} disabled={saving}
                            data-testid={`ca-save-${d}`}
                            className="inline-flex items-center gap-1 text-[11px] font-heading font-semibold px-3 py-1 rounded-full border border-brand-purple/25 bg-white text-brand-purple hover:bg-brand-purpleLight/60 disabled:opacity-50 transition"
                          >
                            <Save className="w-3.5 h-3.5" /> Save
                          </button>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.students.map((s, i) => (
                  <tr key={s.id} className={i % 2 ? "bg-white" : "bg-[#FAFBFE]"} data-testid={`ca-row-${s.id}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap sticky left-0 bg-inherit z-10">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-heading font-semibold text-foreground uppercase">{s.full_name}</span>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="ghost" size="icon" onClick={() => setViewing(s)} className="h-8 w-8 rounded-lg text-brand-inkMuted hover:text-brand-purple" title="View history" data-testid={`ca-view-${s.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setEditing(s)} className="h-8 w-8 rounded-lg text-brand-inkMuted hover:text-brand-purple" title="Edit week" data-testid={`ca-edit-${s.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setMakeUp(s)} className="h-8 w-8 rounded-lg text-brand-inkMuted hover:text-brand-purple" title="Log make-up class" data-testid={`ca-makeup-${s.id}`}>
                            <CalendarPlus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </td>
                    {visibleDates.map((d) => {
                      const cur = draft[d]?.[s.id] || "";
                      const locked = isSaved(d, s.id);
                      return (
                        <td key={d} className="px-2 py-2 text-center">
                          <div className="inline-flex gap-1 items-center" role="group">
                            {STATUS.map((st) => {
                              const active = cur === st.id;
                              return (
                                <button
                                  type="button"
                                  key={st.id}
                                  onClick={() => setMark(d, s.id, st.id)}
                                  disabled={locked && !active}
                                  title={locked ? `${st.title} (saved — use Edit to change)` : st.title}
                                  data-testid={`ca-mark-${s.id}-${d}-${st.id}`}
                                  className={`w-7 h-7 rounded-lg text-[12px] font-heading font-bold border transition ${active ? st.active : st.cls} ${locked && !active ? "opacity-30 cursor-not-allowed" : ""}`}
                                >{st.label}</button>
                              );
                            })}
                            {locked && <Lock className="w-3 h-3 text-brand-inkMuted/60 ml-0.5" />}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {data?.students?.length > 0 && (
        <div className="flex justify-end mt-6">
          <Button
            onClick={saveAll}
            disabled={saving}
            size="lg"
            className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-14 px-10 text-base shadow-md hover:shadow-lg transition-shadow"
            data-testid="ca-save-all"
          >
            <Save className="w-5 h-5 mr-2" /> {saving ? "Saving..." : "Save Week"}
          </Button>
        </div>
      )}

      {!selectedBranch && (
        <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl mt-4" data-testid="no-branch-picked">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
            <AlertCircle className="w-7 h-7 text-[#E65100]" />
          </div>
          <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
        </div>
      )}

      {viewing && (
        <ViewHistoryDialog
          student={viewing}
          branchId={branchId}
          sectionId={sectionId}
          onClose={() => setViewing(null)}
        />
      )}
      {editing && (
        <EditWeekDialog
          student={editing}
          branchId={branchId}
          sectionId={sectionId}
          weekDates={visibleDates}
          initialMarks={Object.fromEntries(visibleDates.map((d) => [d, data?.marks?.[d]?.[editing.id] || ""]))}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadWeek(); }}
        />
      )}
      {makeUp && (
        <MakeUpDialog
          student={makeUp}
          branchId={branchId}
          sectionId={sectionId}
          onClose={() => setMakeUp(null)}
          onSaved={() => setMakeUp(null)}
        />
      )}

      <style>{`
        .ca-select {
          width: 100%; height: 2.75rem; border-radius: 1rem;
          border: 1px solid rgba(37, 99, 235, 0.18); background: #fff;
          padding: 0 0.75rem; font-size: 0.9rem; color: hsl(var(--foreground));
        }
        .ca-select:focus-visible { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring)); }
      `}</style>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading">{label}</p>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ReadInput({ value }) {
  return <div className="ca-select flex items-center cursor-not-allowed text-foreground bg-white">{value}</div>;
}

function Stat({ label, value, tone }) {
  const tones = {
    default: "bg-white border-brand-purple/10 text-foreground",
    green: "bg-[#E8F8EE] border-[#118D57]/25 text-[#118D57]",
    red: "bg-[#FDECEA] border-[#C62828]/25 text-[#C62828]",
    orange: "bg-[#FFF3E0] border-[#E65100]/25 text-[#E65100]",
  };
  return (
    <div className={`rounded-2xl border px-4 py-3 ${tones[tone] || tones.default}`} data-testid={`ca-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-[11px] uppercase tracking-wider font-heading opacity-80">{label}</p>
      <p className="font-heading text-2xl font-bold mt-1 tabular-nums">{value}</p>
    </div>
  );
}

function Empty({ title, subtitle }) {
  return (
    <div className="p-12 text-center" data-testid="ca-empty">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
        <ClipboardCheck className="w-7 h-7 text-brand-purple" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">{title}</p>
      {subtitle && <p className="text-brand-inkMuted mt-1 text-sm">{subtitle}</p>}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* View Attendance History dialog (last 90 days for one student)              */
/* -------------------------------------------------------------------------- */
function ViewHistoryDialog({ student, branchId, sectionId, onClose }) {
  const [rows, setRows] = useState([]);
  const [makeups, setMakeups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMakeup, setEditingMakeup] = useState(null);

  const refetchMakeups = useCallback(async () => {
    try {
      const { data } = await api.get("/class-attendance/make-up", {
        params: { branch_id: branchId, section_id: sectionId, student_id: student.id },
      });
      setMakeups(data || []);
    } catch (e) {
      devWarn("ClassAttendance: make-up fetch failed", e?.message);
    }
  }, [branchId, sectionId, student.id]);

  const handleDeleteMakeup = async (m) => {
    if (!window.confirm(`Delete make-up class on ${m.date}?`)) return;
    try {
      await api.delete(`/class-attendance/make-up/${m.id}`);
      toast.success("Make-up class deleted");
      setMakeups((arr) => arr.filter((x) => x.id !== m.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      // Fetch make-up records once
      try {
        const { data: mu } = await api.get("/class-attendance/make-up", {
          params: { branch_id: branchId, section_id: sectionId, student_id: student.id },
        });
        if (!cancelled) setMakeups(mu || []);
      } catch (e) {
        devWarn("ClassAttendance: make-up fetch failed", e?.message);
      }
      // Fetch the last 13 weeks (~3 months) of attendance and filter to this student
      const today = new Date();
      const monday = getMonday(today);
      const out = [];
      for (let w = 0; w < 13; w++) {
        const ws = new Date(monday); ws.setDate(ws.getDate() - 7 * w);
        const wsISO = fmtISO(ws);
        try {
          const { data } = await api.get("/class-attendance", {
            params: { branch_id: branchId, section_id: sectionId, week_start: wsISO },
          });
          for (const d of data.dates) {
            const mark = data.marks?.[d]?.[student.id];
            if (mark) out.push({ date: d, status: mark });
          }
        } catch (e) {
          devWarn("ClassAttendance: weekly fetch failed for", wsISO, e?.message);
        }
        if (cancelled) return;
      }
      out.sort((a, b) => (a.date < b.date ? 1 : -1));
      if (!cancelled) { setRows(out); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [branchId, sectionId, student.id]);

  const counts = useMemo(() => {
    const c = { present: 0, absent: 0, excuse: 0 };
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md rounded-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="ca-view-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase">{student.full_name}</DialogTitle>
          <DialogDescription className="text-brand-inkMuted">Attendance history — last 13 weeks</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2 my-3">
          <StatChip label="Present" value={counts.present} tone="green" />
          <StatChip label="Absent" value={counts.absent} tone="red" />
          <StatChip label="Excuse" value={counts.excuse} tone="orange" />
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? <p className="text-brand-inkMuted p-3">Loading history...</p>
          : rows.length === 0 && makeups.length === 0 ? <p className="text-brand-inkMuted p-3 italic">No attendance records yet for this student.</p>
          : (
            <>
              {makeups.length > 0 && (
                <div className="mb-3">
                  <p className="text-[11px] uppercase tracking-wider text-brand-purple font-heading mb-1.5">Make-up Classes</p>
                  <ul className="divide-y divide-brand-purple/10 rounded-2xl border border-brand-purple/15 bg-brand-purpleLight/30" data-testid="ca-makeup-list">
                    {makeups.map((m) => (
                      <li key={m.id} className="flex items-center justify-between px-3 py-2.5 text-sm gap-2" data-testid={`ca-makeup-row-${m.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="font-heading text-foreground">{parseISO(m.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</p>
                          {(m.time_start || m.time_end) && (
                            <p className="text-xs text-brand-inkMuted mt-0.5"><Clock className="w-3 h-3 inline -mt-0.5 mr-1" />{m.time_start}{m.time_end ? ` – ${m.time_end}` : ""}</p>
                          )}
                          {m.note && <p className="text-xs text-brand-inkMuted mt-0.5 line-clamp-2">{m.note}</p>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="inline-flex items-center gap-1 text-[11px] font-heading text-brand-purple mr-1"><CalendarPlus className="w-3 h-3" />Make-up</span>
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => setEditingMakeup(m)}
                            className="h-8 w-8 rounded-lg text-brand-inkMuted hover:text-brand-purple hover:bg-brand-purpleLight/60"
                            title="Edit make-up"
                            data-testid={`ca-makeup-edit-${m.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => handleDeleteMakeup(m)}
                            className="h-8 w-8 rounded-lg text-brand-inkMuted hover:text-[#C62828] hover:bg-[#FDECEA]"
                            title="Delete make-up"
                            data-testid={`ca-makeup-delete-${m.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <ul className="divide-y divide-brand-purple/10" data-testid="ca-history-list">
                {rows.map((r) => (
                  <li key={r.date} className="flex items-center justify-between py-2.5">
                    <span className="font-heading text-sm text-foreground">{parseISO(r.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</span>
                    <HistoryPill status={r.status} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <DialogFooter className="pt-3">
          <Button onClick={onClose} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11 px-5">Close</Button>
        </DialogFooter>
      </DialogContent>
      {editingMakeup && (
        <MakeUpDialog
          student={student}
          branchId={branchId}
          sectionId={sectionId}
          editing={editingMakeup}
          onClose={() => setEditingMakeup(null)}
          onSaved={() => { setEditingMakeup(null); refetchMakeups(); }}
        />
      )}
    </Dialog>
  );
}

function StatChip({ label, value, tone }) {
  const tones = {
    green: "bg-[#E8F8EE] text-[#118D57]",
    red: "bg-[#FDECEA] text-[#C62828]",
    orange: "bg-[#FFF3E0] text-[#E65100]",
  };
  return (
    <div className={`rounded-2xl px-3 py-2 text-center ${tones[tone]}`}>
      <p className="text-[10px] uppercase tracking-wider font-heading opacity-80">{label}</p>
      <p className="font-heading text-xl font-bold mt-0.5 tabular-nums">{value}</p>
    </div>
  );
}

function HistoryPill({ status }) {
  const map = {
    present: ["bg-[#22C55E]", "Present"],
    absent: ["bg-[#EF4444]", "Absent"],
    excuse: ["bg-[#F59E0B]", "Excuse"],
  };
  const [bg, label] = map[status] || ["bg-brand-inkMuted", status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-heading font-semibold text-white ${bg}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-white/80" /> {label}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Edit Week dialog (per student, for the currently visible week)             */
/* -------------------------------------------------------------------------- */
function EditWeekDialog({ student, branchId, sectionId, weekDates, initialMarks, onClose, onSaved }) {
  const [marks, setMarks] = useState(initialMarks);
  const [saving, setSaving] = useState(false);

  const setOne = (date, status) => {
    setMarks((prev) => ({ ...prev, [date]: prev[date] === status ? "" : status }));
  };

  const save = async () => {
    setSaving(true);
    try {
      // For each date in the week, fetch the existing day's marks, replace ONLY
      // this student's mark, and PUT back. This keeps other students intact.
      for (const date of weekDates) {
        const { data } = await api.get("/class-attendance", {
          params: { branch_id: branchId, section_id: sectionId, week_start: weekDates[0] },
        });
        const current = { ...(data.marks?.[date] || {}) };
        const newVal = marks[date] || "";
        if (newVal) current[student.id] = newVal; else delete current[student.id];
        await api.put(`/class-attendance?branch_id=${branchId}`, {
          section_id: sectionId, date, marks: current,
        });
      }
      toast.success(`Attendance updated for ${student.full_name}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid="ca-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase">{student.full_name}</DialogTitle>
          <DialogDescription className="text-brand-inkMuted">Edit attendance for this week</DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-brand-purple/10 my-2">
          {weekDates.map((d) => (
            <li key={d} className="flex items-center justify-between py-2.5">
              <span className="font-heading text-sm text-foreground">
                {parseISO(d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
              </span>
              <div className="flex gap-1.5">
                {STATUS.map((st) => {
                  const active = marks[d] === st.id;
                  return (
                    <button
                      key={st.id} type="button"
                      onClick={() => setOne(d, st.id)}
                      title={st.title}
                      data-testid={`ca-edit-mark-${d}-${st.id}`}
                      className={`w-8 h-8 rounded-lg text-[12px] font-heading font-bold border transition ${active ? st.active : st.cls}`}
                    >{st.label}</button>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl h-11">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11 px-6" data-testid="ca-edit-save">
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/* Make-up Class Dialog                                                       */
/* -------------------------------------------------------------------------- */
function MakeUpDialog({ student, branchId, sectionId, editing, onClose, onSaved }) {
  const [date, setDate] = useState(editing?.date || fmtISO(new Date()));
  const [timeStart, setTimeStart] = useState(editing?.time_start || "");
  const [timeEnd, setTimeEnd] = useState(editing?.time_end || "");
  const [note, setNote] = useState(editing?.note || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Date is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/class-attendance/make-up/${editing.id}`, {
          date,
          time_start: timeStart,
          time_end: timeEnd,
          note,
        });
        toast.success("Make-up class updated");
      } else {
        await api.post(`/class-attendance/make-up?branch_id=${branchId}`, {
          section_id: sectionId,
          student_id: student.id,
          date,
          time_start: timeStart,
          time_end: timeEnd,
          note,
        });
        toast.success("Make-up class logged");
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid="ca-makeup-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{editing ? "Edit Make-up Class" : "Make-up Class Attendance"}</DialogTitle>
          <DialogDescription className="text-brand-inkMuted uppercase">{student.full_name}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="font-heading">Date <span className="text-[#C62828]">*</span></Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-2xl h-11 mt-1.5" data-testid="ca-makeup-date" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-heading">Time Start</Label>
              <Input type="time" value={timeStart} onChange={(e) => setTimeStart(e.target.value)} className="rounded-2xl h-11 mt-1.5" data-testid="ca-makeup-start" />
            </div>
            <div>
              <Label className="font-heading">Time End</Label>
              <Input type="time" value={timeEnd} onChange={(e) => setTimeEnd(e.target.value)} className="rounded-2xl h-11 mt-1.5" data-testid="ca-makeup-end" />
            </div>
          </div>
          <div>
            <Label className="font-heading">Note</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Make-up for May 18 absence" className="rounded-2xl mt-1.5 min-h-[80px]" data-testid="ca-makeup-note" />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl h-11">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11 px-6" data-testid="ca-makeup-submit">{saving ? "Saving..." : (editing ? "Save changes" : "Log Make-up")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

